import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import * as Yup from "yup";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { FieldArray, FormikProvider, useFormik } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../../components/BasicModal";
import FormModalActions from "../../../components/FormModalActions";
import {
  LARGE_FORM_MODAL_SX,
  DENSE_FORM_BODY_SX,
  COMPACT_MOBILE_TIME_PICKER_SLOT_PROPS,
  STANDARD_FORM_DIALOG_PROPS,
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
} from "../../../utils/formModalStyles";
import { getAllDepartment } from "../../../services/departmentService"; 
import { getAllUserRoles } from "../../../services/UserRoleService"; 
import { format } from "date-fns";
import { createDoctor, updateDoctor } from "../../../services/doctorService";
import {
  validateOrgTeamUserFields,
  touchFormikErrorFields,
  buildDoctorExcludeContext,
} from "../../../utils/orgUserUniqueness";
import subscriptionRoleLimitsService from "../../../services/subscriptionRoleLimitsService";
import subscriptionHistoryService from "../../../services/subscriptionHistoryService";
import { DeleteForeverOutlined } from "@mui/icons-material";
import { useAuth } from "../../../context/AuthContext"; 
import { useSubscriptionCapabilities } from "../../../hooks/useSubscriptionCapabilities";
import { filterDepartmentsForPackage, filterDoctorRolesForPackage } from "../../../utils/subscriptionScopeUtils";

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name should be of minimum 2 characters length"),

  email: Yup.string()
    .email("Enter a valid email"),

  education: Yup.string().required("Education is required"),

  specialty: Yup.string().required("Specialty is required"),

  aadhar: Yup.string()
    .required("Aadhar is required")
    .matches(/^\d{12}$/, "Aadhar should be a 12-digit number"),

  mobile: Yup.string()
    .required("Contact Number is required")
    .matches(/^\d{10}$/, "Contact Number should be a 10-digit number"),

  registrationNumber: Yup.string().required("Registration Number is required"),

  departmentId: Yup.array()
    .min(1, "Department is required")
    .required("Department is required"),

  joiningDate: Yup.date().required("Joining Date is required"),

  address: Yup.string().required("Address is required"),

  userRole: Yup.string().required("User Role is required"),

  visitingSchedules: Yup.array()
    .of(
      Yup.object({
        day: Yup.string().nullable(),
        start_time: Yup.mixed().nullable().test(
          "start-time-required",
          "Start Time is required",
          function (value) {
            const { day } = this.parent;
            // Skip validation if day is empty
            if (!day || typeof day !== 'string' || day.trim() === "") {
              return true;
            }
            
            // For "Weekdays" and "Weekend", times are required
            if (day === "Weekdays" || day === "Weekend") {
              if (value === null || value === undefined || value === "") {
                return false;
              }
              
              // For dayjs objects, check if they're valid
              if (value && typeof value === 'object') {
                if (typeof value.isValid === 'function' && typeof value.format === 'function') {
                  return value.isValid() === true;
                }
                if (value instanceof Date) {
                  return !isNaN(value.getTime());
                }
              }
              
              return true;
            }
            
            // For individual days, time is required
            if (value === null || value === undefined || value === "") {
              return false;
            }
            
            // For dayjs objects, check if they're valid
            if (value && typeof value === 'object') {
              if (typeof value.isValid === 'function' && typeof value.format === 'function') {
                return value.isValid() === true;
              }
              if (value instanceof Date) {
                return !isNaN(value.getTime());
              }
            }
            
            return true;
          }
        ),
        end_time: Yup.mixed().nullable().test(
          "end-time-required",
          "End Time is required",
          function (value) {
            const { day } = this.parent;
            // Skip validation if day is empty
            if (!day || typeof day !== 'string' || day.trim() === "") {
              return true;
            }
            
            // For "Weekdays" and "Weekend", times are required
            if (day === "Weekdays" || day === "Weekend") {
              if (value === null || value === undefined || value === "") {
                return false;
              }
              
              // For dayjs objects, check if they're valid
              if (value && typeof value === 'object') {
                if (typeof value.isValid === 'function' && typeof value.format === 'function') {
                  return value.isValid() === true;
                }
                if (value instanceof Date) {
                  return !isNaN(value.getTime());
                }
              }
              
              return true;
            }
            
            // For individual days, time is required
            if (value === null || value === undefined || value === "") {
              return false;
            }
            
            // For dayjs objects, check if they're valid
            if (value && typeof value === 'object') {
              if (typeof value.isValid === 'function' && typeof value.format === 'function') {
                return value.isValid() === true;
              }
              if (value instanceof Date) {
                return !isNaN(value.getTime());
              }
            }
            
            return true;
          }
        ),
      })
    )
    .test(
      "at-least-one-schedule",
      "At least one visiting schedule with day, start time, and end time is required",
      function (schedules) {
        if (!schedules || schedules.length === 0) {
          return false;
        }
        // Check if at least one schedule has all required fields
        const hasCompleteSchedule = schedules.some(
          (schedule) => {
            if (!schedule) return false;
            
            const day = schedule.day;
            const startTime = schedule.start_time;
            const endTime = schedule.end_time;
            
            // Day must be set (can be individual day, "Weekdays", or "Weekend")
            if (!day || typeof day !== 'string' || day.trim() === "") {
              return false;
            }
            
            // Both times must be set and valid
            const hasStartTime = startTime != null && startTime !== "";
            const hasEndTime = endTime != null && endTime !== "";
            
            if (!hasStartTime || !hasEndTime) {
              return false;
            }
            
            // If times are dayjs objects, check if they're valid
            if (startTime && typeof startTime === 'object' && startTime.isValid && typeof startTime.isValid === 'function') {
              if (!startTime.isValid()) return false;
            }
            if (endTime && typeof endTime === 'object' && endTime.isValid && typeof endTime.isValid === 'function') {
              if (!endTime.isValid()) return false;
            }
            
            return true;
          }
        );
        return hasCompleteSchedule;
      }
    ),
});

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "Weekdays", // Monday to Friday
  "Weekend",  // Saturday and Sunday
];
const AddNewDoctor = ({ open, onClose, doctor_info = {} }) => {
  const { user } = useAuth();
  const { isPremiumPackage } = useSubscriptionCapabilities();
  const [department_list, setDepartmentList] = useState([]);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdDoctor, setCreatedDoctor] = useState({ mobile: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [roleLimitCheck, setRoleLimitCheck] = useState(null);
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState(null);

  const visibleDepartments = useMemo(() => {
    const keepIds = [];
    if (doctor_info?.departmentId) keepIds.push(doctor_info.departmentId);
    if (Array.isArray(doctor_info?.departmentIds)) {
      keepIds.push(...doctor_info.departmentIds);
    }
    return filterDepartmentsForPackage(department_list, {
      isPremium: isPremiumPackage || subscriptionType === "PREMIUM",
      keepIds,
    });
  }, [department_list, isPremiumPackage, subscriptionType, doctor_info]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (open && user?.organizationId) {
      fetchSubscriptionType();
    }
  }, [open, user?.organizationId]);

  const fetchSubscriptionType = async () => {
    if (!user?.organizationId) return;
    try {
      const subscription = await subscriptionHistoryService.getActiveSubscription(user.organizationId);
      if (subscription) {
        const typeName = subscription.subscriptionType?.subType || 
                         subscription.subscriptionType || 
                         null;
        setSubscriptionType(typeName);
      }
    } catch (error) {
      console.error("Error fetching subscription type:", error);
    }
  };

  useEffect(() => {
    if (!doctor_info) return;
    const detailName = (doctor_info["departmentName"] || "").trim().toLowerCase();
    const detailId = doctor_info["departmentId"] != null ? Number(doctor_info["departmentId"]) : null;
    const detailIds = doctor_info["departmentIds"] || [];

    // If departmentIds array is available, use it directly
    if (Array.isArray(detailIds) && detailIds.length > 0) {
      const departmentIds = detailIds.map(id => Number(id));
      formik.setFieldValue("departmentId", departmentIds, false);
      formik.setFieldValue("departmentName", doctor_info["departmentName"] || "", false);
      return;
    }

    // Prefer matching by name when available to keep UI consistent with detail response
    if (Array.isArray(department_list) && department_list.length && detailName) {
      const byName = department_list.find(
        (d) => (d?.name || "").trim().toLowerCase() === detailName
      );
      if (byName) {
        formik.setFieldValue("departmentId", [Number(byName.id)], false);
        formik.setFieldValue("departmentName", byName.name, false);
        return;
      }
    }

    // Fallback: use departmentId to resolve name if possible
    if (detailId != null) {
      formik.setFieldValue("departmentId", [detailId], false);
      if (Array.isArray(department_list) && department_list.length) {
        const byId = department_list.find((d) => Number(d.id) === detailId);
        formik.setFieldValue(
          "departmentName",
          byId?.name || doctor_info["departmentName"] || "",
          false
        );
      } else {
        formik.setFieldValue("departmentName", doctor_info["departmentName"] || "", false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctor_info, department_list]);

  // Separate useEffect to handle visiting schedules initialization
  useEffect(() => {
    if (!doctor_info || !doctor_info.visitingSchedules) {
      console.log(`[AddNewDoctor] No doctor_info or visitingSchedules, skipping`);
      return;
    }

    console.log(`[AddNewDoctor] useEffect - Processing visitingSchedules from doctor_info:`, doctor_info.visitingSchedules);
    
    const schedules = doctor_info.visitingSchedules;
    let processedSchedules = [];

    // Case 1: Array format
    if (Array.isArray(schedules) && schedules.length > 0) {
      console.log(`[AddNewDoctor] useEffect - Array format detected with ${schedules.length} items`);
      
      // First, process all schedules to get individual days with times
      const allSchedules = schedules.map((schedule) => {
        const dayOfWeek = schedule.dayOfWeek || schedule.day || schedule.day_of_week || "";
        const startTime = schedule.startTime || schedule.start_time || "";
        const endTime = schedule.endTime || schedule.end_time || "";
        
        const day = dayOfWeek 
          ? dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1).toLowerCase()
          : "";
        
        // Parse times using helper function
        let parsedStartTime = null;
        let parsedEndTime = null;
        
        if (startTime) {
          if (typeof startTime === 'string') {
            parsedStartTime = parseTimeString(startTime);
          } else {
            parsedStartTime = dayjs(startTime);
          }
        }
        
        if (endTime) {
          if (typeof endTime === 'string') {
            parsedEndTime = parseTimeString(endTime);
          } else {
            parsedEndTime = dayjs(endTime);
          }
        }
        
        return {
          day,
          start_time: parsedStartTime?.isValid() ? parsedStartTime : null,
          end_time: parsedEndTime?.isValid() ? parsedEndTime : null,
        };
      }).filter(schedule => schedule.day);
      
      // Now collapse weekdays and weekend if they have same times
      const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
      const weekendDays = ["Saturday", "Sunday"];
      
      // Check if all weekdays have the same times
      const weekdaySchedules = allSchedules.filter(s => weekdays.includes(s.day));
      const weekendSchedules = allSchedules.filter(s => weekendDays.includes(s.day));
      
      // Check if all weekdays have same start and end times
      if (weekdaySchedules.length === 5) {
        const firstWeekday = weekdaySchedules[0];
        const allSameTimes = weekdaySchedules.every(s => {
          const startMatch = firstWeekday.start_time?.format("HH:mm") === s.start_time?.format("HH:mm");
          const endMatch = firstWeekday.end_time?.format("HH:mm") === s.end_time?.format("HH:mm");
          return startMatch && endMatch;
        });
        
        if (allSameTimes && firstWeekday.start_time && firstWeekday.end_time) {
          // Collapse to "Weekdays"
          processedSchedules.push({
            day: "Weekdays",
            start_time: firstWeekday.start_time,
            end_time: firstWeekday.end_time,
          });
        } else {
          // Keep individual days
          processedSchedules.push(...weekdaySchedules);
        }
      } else {
        // Keep individual weekdays if not all 5 are present
        processedSchedules.push(...weekdaySchedules);
      }
      
      // Check if both weekend days have same times
      if (weekendSchedules.length === 2) {
        const firstWeekend = weekendSchedules[0];
        const allSameTimes = weekendSchedules.every(s => {
          const startMatch = firstWeekend.start_time?.format("HH:mm") === s.start_time?.format("HH:mm");
          const endMatch = firstWeekend.end_time?.format("HH:mm") === s.end_time?.format("HH:mm");
          return startMatch && endMatch;
        });
        
        if (allSameTimes && firstWeekend.start_time && firstWeekend.end_time) {
          // Collapse to "Weekend"
          processedSchedules.push({
            day: "Weekend",
            start_time: firstWeekend.start_time,
            end_time: firstWeekend.end_time,
          });
        } else {
          // Keep individual days
          processedSchedules.push(...weekendSchedules);
        }
      } else {
        // Keep individual weekend days if not both are present
        processedSchedules.push(...weekendSchedules);
      }
      
      // Add any other individual days (not weekdays or weekend)
      const otherDays = allSchedules.filter(s => 
        !weekdays.includes(s.day) && !weekendDays.includes(s.day)
      );
      processedSchedules.push(...otherDays);
    }
    // Case 2: Object format
    else if (typeof schedules === 'object' && !Array.isArray(schedules)) {
      console.log(`[AddNewDoctor] useEffect - Object format detected`);
      processedSchedules = Object.keys(schedules).map((day) => {
        const schedule = schedules[day];
        const dayName = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
        const startTime = schedule.startTime || schedule.start_time || "";
        const endTime = schedule.endTime || schedule.end_time || "";
        
        let parsedStartTime = null;
        let parsedEndTime = null;
        
        if (startTime) {
          if (typeof startTime === 'string') {
            parsedStartTime = parseTimeString(startTime);
          } else {
            parsedStartTime = dayjs(startTime);
          }
        }
        
        if (endTime) {
          if (typeof endTime === 'string') {
            parsedEndTime = parseTimeString(endTime);
          } else {
            parsedEndTime = dayjs(endTime);
          }
        }
        
        console.log(`[AddNewDoctor] useEffect - Processed schedule for ${dayName}:`, {
          startTime,
          parsedStartTime: parsedStartTime?.isValid() ? parsedStartTime.format("HH:mm") : "INVALID",
          endTime,
          parsedEndTime: parsedEndTime?.isValid() ? parsedEndTime.format("HH:mm") : "INVALID"
        });
        
        return {
          day: dayName,
          start_time: parsedStartTime && (parsedStartTime.isValid ? parsedStartTime.isValid() : true) ? parsedStartTime : null,
          end_time: parsedEndTime && (parsedEndTime.isValid ? parsedEndTime.isValid() : true) ? parsedEndTime : null,
        };
      }).filter(schedule => schedule.day);
    }
    
    // Set the visiting schedules if we have valid ones
    if (processedSchedules.length > 0) {
      console.log(`[AddNewDoctor] useEffect - Setting ${processedSchedules.length} visiting schedules:`, processedSchedules);
      console.log(`[AddNewDoctor] useEffect - Schedule details:`, processedSchedules.map(s => ({
        day: s.day,
        start_time: s.start_time?.format ? s.start_time.format("HH:mm") : s.start_time,
        end_time: s.end_time?.format ? s.end_time.format("HH:mm") : s.end_time,
        start_time_type: typeof s.start_time,
        end_time_type: typeof s.end_time
      })));
      formik.setFieldValue("visitingSchedules", processedSchedules, false);
      
      // Verify it was set
      setTimeout(() => {
        console.log(`[AddNewDoctor] useEffect - Verification - formik.values.visitingSchedules:`, formik.values.visitingSchedules);
      }, 100);
    } else {
      console.warn(`[AddNewDoctor] useEffect - No valid visiting schedules found, keeping default`);
      console.warn(`[AddNewDoctor] useEffect - Raw schedules data:`, schedules);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctor_info]);

  const fetchDepartments = async () => {
    setDepartmentList([]);
    try {
      const data = await getAllDepartment();
      setDepartmentList(data);
    } catch (error) {
      console.log(error);
    }
  };

  const [role_list, setRoleList] = useState([]);
  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setRoleList([]);
    try {
      const data = await getAllUserRoles();
      setRoleList(data);
    } catch (error) {
      console.log(error);
    }
  };

  // const isUnique = async (field, value, currentDoctorId = null) => {
  //   try {
  //     const allDoctors = await getAllDoctors();

  //     return !allDoctors.some(
  //       (doctor) => doctor[field] === value && doctor.id !== currentDoctorId
  //     );
  //   } catch (error) {
  //     console.error("Uniqueness check failed:", error);
  //     return true;
  //   }
  // };

  // Helper function to parse time string to dayjs object
  const parseTimeString = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') {
      return null;
    }
    
    const cleanTime = timeStr.trim();
    
    // Try "hh:mm A" format first (12-hour with AM/PM) - most common format like "10:00 AM"
    let parsed = dayjs(cleanTime, "hh:mm A", true);
    if (parsed.isValid()) {
      return parsed;
    }
    
    // Try "h:mm A" format (single digit hour with AM/PM) like "9:00 AM"
    parsed = dayjs(cleanTime, "h:mm A", true);
    if (parsed.isValid()) {
      return parsed;
    }
    
    // Try "HH:mm" format (24-hour) like "10:00"
    parsed = dayjs(cleanTime, "HH:mm", true);
    if (parsed.isValid()) {
      return parsed;
    }
    
    // Try "H:mm" format (24-hour single digit) like "9:00"
    parsed = dayjs(cleanTime, "H:mm", true);
    if (parsed.isValid()) {
      return parsed;
    }
    
    // Try "hh:mm" format (12-hour without AM/PM) like "10:00"
    parsed = dayjs(cleanTime, "hh:mm", true);
    if (parsed.isValid()) {
      return parsed;
    }
    
    // Manual parsing for "hh:mm A" or "h:mm A" format if dayjs fails
    // This handles cases like "10:00 AM", "07:00 PM", "7:30 PM"
    const timeMatch = cleanTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      const ampm = timeMatch[3].toUpperCase();
      
      // Convert to 24-hour format
      if (ampm === 'PM' && hours !== 12) {
        hours += 12;
      } else if (ampm === 'AM' && hours === 12) {
        hours = 0;
      }
      
      // Create dayjs object with today's date and parsed time
      // MobileTimePicker needs a dayjs object with date and time
      const today = dayjs();
      return today.hour(hours).minute(minutes).second(0).millisecond(0);
    }
    
    // Last resort: try parsing as-is
    parsed = dayjs(cleanTime);
    return parsed.isValid() ? parsed : null;
  };

  const formik = useFormik({
    initialValues: {
      name: doctor_info?.["name"] ? 
        (doctor_info["name"].startsWith("Dr. ") ? 
          doctor_info["name"].substring(4) : 
          doctor_info["name"]) : "",
      address: doctor_info?.["address"] || "",
      aadhar: doctor_info?.["aadhar"] || "",
      departmentId: doctor_info?.["departmentIds"] != null ? 
        doctor_info["departmentIds"].map(id => Number(id)) : 
        (doctor_info?.["departmentId"] != null ? 
          (Array.isArray(doctor_info["departmentId"]) ? 
            doctor_info["departmentId"].map(id => Number(id)) : 
            [Number(doctor_info["departmentId"])]) : []),
      departmentName: doctor_info?.["departmentName"] || "",  
      education: doctor_info?.["education"] || "",
      password: doctor_info?.["password"] || "Doctor@123",
      email: doctor_info?.["email"] || "",
      joiningDate: doctor_info?.["joiningDate"]
        ? dayjs(doctor_info["joiningDate"])
        : null,
      mobile: doctor_info?.["mobile"] || "",
      registrationNumber: doctor_info?.["registrationNumber"] || "",
      specialty: doctor_info?.["specialty"] || "",
      userRole: doctor_info?.["userRole"] || "",
      visitingSchedules: (() => {
        const schedules = doctor_info?.["visitingSchedules"];
        
        if (!schedules) {
          return [{ day: "", start_time: null, end_time: null }];
        }
        
        let allSchedules = [];
        
        // Case 1: Array format (e.g., [{ dayOfWeek: "monday", startTime: "09:00", endTime: "17:00" }])
        if (Array.isArray(schedules) && schedules.length > 0) {
          allSchedules = schedules.map((schedule) => {
            // Handle different property names
            const dayOfWeek = schedule.dayOfWeek || schedule.day || schedule.day_of_week || "";
            const startTime = schedule.startTime || schedule.start_time || "";
            const endTime = schedule.endTime || schedule.end_time || "";
            
            // Capitalize day name
            const day = dayOfWeek 
              ? dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1).toLowerCase()
              : "";
            
            // Parse time - try different formats
            let parsedStartTime = null;
            let parsedEndTime = null;
            
            if (startTime) {
              if (typeof startTime === 'string') {
                parsedStartTime = parseTimeString(startTime);
              } else {
                parsedStartTime = dayjs(startTime);
              }
            }
            
            if (endTime) {
              if (typeof endTime === 'string') {
                parsedEndTime = parseTimeString(endTime);
              } else {
                parsedEndTime = dayjs(endTime);
              }
            }
            
            return {
              day,
              start_time: parsedStartTime?.isValid() ? parsedStartTime : null,
              end_time: parsedEndTime?.isValid() ? parsedEndTime : null,
            };
          }).filter(schedule => schedule.day); // Filter out empty days
        }
        // Case 2: Object format (e.g., { "monday": { start_time: "09:00 AM", end_time: "05:00 PM" } })
        else if (typeof schedules === 'object' && !Array.isArray(schedules)) {
          allSchedules = Object.keys(schedules).map((day) => {
            const schedule = schedules[day];
            const dayName = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
            
            // Handle different property names
            const startTime = schedule.startTime || schedule.start_time || "";
            const endTime = schedule.endTime || schedule.end_time || "";
            
            // Parse time - try different formats
            let parsedStartTime = null;
            let parsedEndTime = null;
            
            if (startTime) {
              if (typeof startTime === 'string') {
                parsedStartTime = parseTimeString(startTime);
              } else {
                parsedStartTime = dayjs(startTime);
              }
            }
            
            if (endTime) {
              if (typeof endTime === 'string') {
                parsedEndTime = parseTimeString(endTime);
              } else {
                parsedEndTime = dayjs(endTime);
              }
            }
            
            return {
              day: dayName,
              start_time: parsedStartTime && (parsedStartTime.isValid ? parsedStartTime.isValid() : true) ? parsedStartTime : null,
              end_time: parsedEndTime && (parsedEndTime.isValid ? parsedEndTime.isValid() : true) ? parsedEndTime : null,
            };
          }).filter(schedule => schedule.day); // Filter out empty days
        }
        
        if (allSchedules.length === 0) {
          return [{ day: "", start_time: null, end_time: null }];
        }
        
        // Collapse weekdays and weekend if they have same times
        const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
        const weekendDays = ["Saturday", "Sunday"];
        const processedSchedules = [];
        
        // Check if all weekdays have the same times
        const weekdaySchedules = allSchedules.filter(s => weekdays.includes(s.day));
        const weekendSchedules = allSchedules.filter(s => weekendDays.includes(s.day));
        
        // Check if all weekdays have same start and end times
        if (weekdaySchedules.length === 5) {
          const firstWeekday = weekdaySchedules[0];
          const allSameTimes = weekdaySchedules.every(s => {
            const startMatch = firstWeekday.start_time?.format("HH:mm") === s.start_time?.format("HH:mm");
            const endMatch = firstWeekday.end_time?.format("HH:mm") === s.end_time?.format("HH:mm");
            return startMatch && endMatch;
          });
          
          if (allSameTimes && firstWeekday.start_time && firstWeekday.end_time) {
            // Collapse to "Weekdays"
            processedSchedules.push({
              day: "Weekdays",
              start_time: firstWeekday.start_time,
              end_time: firstWeekday.end_time,
            });
          } else {
            // Keep individual days
            processedSchedules.push(...weekdaySchedules);
          }
        } else {
          // Keep individual weekdays if not all 5 are present
          processedSchedules.push(...weekdaySchedules);
        }
        
        // Check if both weekend days have same times
        if (weekendSchedules.length === 2) {
          const firstWeekend = weekendSchedules[0];
          const allSameTimes = weekendSchedules.every(s => {
            const startMatch = firstWeekend.start_time?.format("HH:mm") === s.start_time?.format("HH:mm");
            const endMatch = firstWeekend.end_time?.format("HH:mm") === s.end_time?.format("HH:mm");
            return startMatch && endMatch;
          });
          
          if (allSameTimes && firstWeekend.start_time && firstWeekend.end_time) {
            // Collapse to "Weekend"
            processedSchedules.push({
              day: "Weekend",
              start_time: firstWeekend.start_time,
              end_time: firstWeekend.end_time,
            });
          } else {
            // Keep individual days
            processedSchedules.push(...weekendSchedules);
          }
        } else {
          // Keep individual weekend days if not both are present
          processedSchedules.push(...weekendSchedules);
        }
        
        // Add any other individual days (not weekdays or weekend)
        const otherDays = allSchedules.filter(s => 
          !weekdays.includes(s.day) && !weekendDays.includes(s.day)
        );
        processedSchedules.push(...otherDays);
        
        return processedSchedules.length > 0 
          ? processedSchedules 
          : [{ day: "", start_time: null, end_time: null }];
      })(),
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    validateOnChange: false,
    validate: async (values) => {
      const excludeContext = buildDoctorExcludeContext(doctor_info);
      return validateOrgTeamUserFields(values, excludeContext, [
        ["aadhar", "mobile"],
        ["email", "registrationNumber"],
      ]);
    },

    onSubmit: async (values, { setFieldError, setFieldTouched }) => {
      // Safety net: re-verify uniqueness with fresh data before creating/updating
      const excludeContext = buildDoctorExcludeContext(doctor_info);
      const duplicateErrors = await validateOrgTeamUserFields(values, excludeContext, [
        ["aadhar", "mobile"],
        ["email", "registrationNumber"],
      ]);
      if (Object.keys(duplicateErrors).length > 0) {
        Object.entries(duplicateErrors).forEach(([field, message]) => {
          setFieldError(field, message);
          setFieldTouched(field, true, false);
        });
        return;
      }

      // Debug: Log visiting schedules before submission
      console.log('[AddNewDoctor] Visiting schedules before submission:', values.visitingSchedules);
      values.visitingSchedules.forEach((schedule, idx) => {
        console.log(`[AddNewDoctor] Schedule ${idx}:`, {
          day: schedule.day,
          start_time: schedule.start_time,
          start_time_type: typeof schedule.start_time,
          start_time_isValid: schedule.start_time?.isValid ? schedule.start_time.isValid() : 'N/A',
          end_time: schedule.end_time,
          end_time_type: typeof schedule.end_time,
          end_time_isValid: schedule.end_time?.isValid ? schedule.end_time.isValid() : 'N/A',
        });
      });
      
      // Get organizationId to ensure it's explicitly included
      const storedUser = localStorage.getItem("user");
      let organizationId = null;
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          organizationId = user?.organizationId || localStorage.getItem("organizationId");
          if (organizationId && typeof organizationId === 'string') {
            organizationId = parseInt(organizationId, 10);
          }
        } catch (e) {
          console.error("[AddNewDoctor] Error parsing stored user:", e);
        }
      }
      
      console.log(`[AddNewDoctor] Submitting with organizationId:`, organizationId);
      
      // Build request data - exclude fields that shouldn't be sent
      const req_Data = {
        name: `Dr. ${values.name}`, // Add "Dr." prefix to the name
        address: values.address,
        aadhar: values.aadhar,
        departmentId: Array.isArray(values.departmentId) ? values.departmentId : [values.departmentId],
        departmentName: values.departmentName,
        education: values.education,
        email: values.email,
        joiningDate: format(new Date(values.joiningDate), "yyyy-MM-dd"),
        mobile: values.mobile,
        registrationNumber: values.registrationNumber,
        specialty: values.specialty,
        userRole: values.userRole,
        visitingSchedules: [], // Will be populated below
        // Explicitly include organizationId to ensure backend uses correct table
        ...(organizationId && organizationId > 0 ? { organizationId } : {}),
      };
      
      // Include password in request
      if (!doctor_info) {
        // New doctor: use provided password or default
        req_Data.password = values.password || "Doctor@123";
      } else {
        // Updating doctor: always include password if it's provided and not empty
        // The password field is only visible to admin/super admin or the doctor themselves
        // If the field is visible and has a value, it should be updated (even if it's the default "Doctor@123")
        if (values.password && values.password.trim()) {
          req_Data.password = values.password.trim();
        }
        // Note: If password is empty/not provided, it won't be included in req_Data
        // This means the backend won't update the password (which is the desired behavior)
      }
      
      // Process visiting schedules - expand "Weekdays" and "Weekend" to individual days for backend
      values.visitingSchedules.forEach((schedule) => {
        // Only process schedules with valid day and times
        if (schedule.day && schedule.start_time && schedule.end_time) {
          try {
            // Format times - handle dayjs objects
            let startTimeStr = "";
            let endTimeStr = "";
            
            if (schedule.start_time && typeof schedule.start_time === 'object' && schedule.start_time.format) {
              // It's a dayjs object
              startTimeStr = schedule.start_time.format("HH:mm");
            } else if (schedule.start_time) {
              // It's already a string or date
              try {
                startTimeStr = format(new Date(schedule.start_time), "HH:mm");
              } catch (e) {
                console.warn(`[AddNewDoctor] Error formatting start_time:`, e);
              }
            }
            
            if (schedule.end_time && typeof schedule.end_time === 'object' && schedule.end_time.format) {
              // It's a dayjs object
              endTimeStr = schedule.end_time.format("HH:mm");
            } else if (schedule.end_time) {
              // It's already a string or date
              try {
                endTimeStr = format(new Date(schedule.end_time), "HH:mm");
              } catch (e) {
                console.warn(`[AddNewDoctor] Error formatting end_time:`, e);
              }
            }
            
            // Only add if we have valid times
            if (startTimeStr && endTimeStr) {
              // Expand "Weekdays" to individual days for backend
              if (schedule.day === "Weekdays") {
                const weekdays = ["monday", "tuesday", "wednesday", "thursday", "friday"];
                weekdays.forEach((day) => {
                  req_Data.visitingSchedules.push({
                    dayOfWeek: day,
                    startTime: startTimeStr,
                    endTime: endTimeStr,
                  });
                });
              }
              // Expand "Weekend" to individual days for backend
              else if (schedule.day === "Weekend") {
                const weekendDays = ["saturday", "sunday"];
                weekendDays.forEach((day) => {
                  req_Data.visitingSchedules.push({
                    dayOfWeek: day,
                    startTime: startTimeStr,
                    endTime: endTimeStr,
                  });
                });
              }
              // Individual day - send as is
              else {
                req_Data.visitingSchedules.push({
                  dayOfWeek: schedule.day.toLowerCase(),
                  startTime: startTimeStr,
                  endTime: endTimeStr,
                });
              }
            }
          } catch (error) {
            console.error(`[AddNewDoctor] Error processing schedule for ${schedule.day}:`, error);
          }
        }
      });
      
      console.log(`[AddNewDoctor] Submitting doctor data:`, {
        ...req_Data,
        visitingSchedulesCount: req_Data.visitingSchedules.length,
        visitingSchedules: req_Data.visitingSchedules
      });
      
      try {
        setLoading(true); 
        if (!!doctor_info) {
          console.log(`[AddNewDoctor] Updating doctor ${doctor_info.id} with organizationId: ${req_Data.organizationId}`);
          await updateDoctor(doctor_info.id, doctor_info.userId, req_Data);
          alert('Doctor updated successfully');
          // Close modal; parent will refetch on onClose
          handelCloseModalState();
        } else {
          // apiClient will automatically add organizationId via interceptor
          const data = await createDoctor(req_Data);
          alert('Doctor created successfully');
          setCreatedDoctor({ mobile: data.mobile, password: "Doctor@123" });
          setShowCredentialsModal(true);
          // For create, close after credentials popup closes
        }
      } catch (error) {
        console.error(`[AddNewDoctor] Error submitting doctor:`, error);
        console.error(`[AddNewDoctor] Error response:`, error.response?.data);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to save doctor';
        alert(`Error: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    },
  });

  // Check subscription role limits when userRole changes (only for new doctors, not updates)
  // This must be after formik initialization
  useEffect(() => {
    const checkRoleLimit = async () => {
      const userRole = formik.values.userRole;
      const organizationId = user?.organizationId;

      if (!userRole || !organizationId) {
        setRoleLimitCheck(null);
        return;
      }

      // Check if this is an update and if the role is the same
      const isUpdate = doctor_info && doctor_info.id;
      const originalRole = doctor_info?.["userRole"];
      const roleChanged = isUpdate && originalRole && originalRole !== userRole;

      setCheckingLimit(true);
      try {
        // For doctors, check "doctor" role limit (both doctor and surgeon use the same limit)
        const roleToCheck = userRole === "surgeon" ? "doctor" : userRole;
        const originalRoleToCheck = originalRole === "surgeon" ? "doctor" : originalRole;
        const limitCheck = await subscriptionRoleLimitsService.checkRoleLimit(
          organizationId,
          roleToCheck
        );
        
        // If updating and role is the same (or both map to "doctor"), allow it (user is already in this role)
        if (isUpdate && !roleChanged && roleToCheck === originalRoleToCheck && limitCheck.limit !== -1) {
          // User is already in this role, so allow the update
          limitCheck.allowed = true;
          limitCheck.message = `Updating existing ${userRole} user. ${limitCheck.message}`;
        }
        
        console.log("[AddNewDoctor] Role limit check result:", limitCheck);
        setRoleLimitCheck(limitCheck);
      } catch (error) {
        console.error("Error checking role limit:", error);
        setRoleLimitCheck(null);
      } finally {
        setCheckingLimit(false);
      }
    };

    // Debounce the check to avoid too many API calls
    const timeoutId = setTimeout(() => {
      checkRoleLimit();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formik.values.userRole, user?.organizationId, doctor_info?.id, doctor_info?.["userRole"]]);

  const handelCloseModalState = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <>
      <BasicModal
        open={open}
        onClose={handelCloseModalState}
        sx={LARGE_FORM_MODAL_SX}
        title={!!doctor_info ? "Update Doctor" : "Add New Doctor"}
      >
        <Box display={"flex"} flexDirection={"column"}>
          <Box sx={DENSE_FORM_BODY_SX}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    id="name"
                    name="name"
                    label="Name"
                    value={formik.values.name}
                    onChange={(e) => {
                      let inputValue = e.target.value;
                      
                      // Remove "Dr." or "Dr " (with period or space) at the beginning only
                      // This allows names like "Dragno" but prevents "Dr. " or "Dr "
                      inputValue = inputValue.replace(/^dr[\.\s]+/gi, '');
                      
                      // Capitalize first letter
                      if (inputValue.length > 0) {
                        inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                      }
                      
                      formik.setFieldValue("name", inputValue);
                    }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mr: 1,
                          color: 'text.secondary',
                          fontWeight: 'bold'
                        }}>
                          Dr.
                        </Box>
                      ),
                    }}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    id="email"
                    name="email"
                    label="Email"
                    value={formik.values.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow email characters including @ symbol (letters, numbers, dots, hyphens, underscores, @, and +)
                      if (value === "" || /^[a-zA-Z0-9._+-@]*$/.test(value)) {
                        formik.setFieldValue("email", value);
                      }
                    }}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    id="education"
                    name="education"
                    label="Education"
                    value={formik.values.education}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.education && Boolean(formik.errors.education)
                    }
                    helperText={
                      formik.touched.education && formik.errors.education
                    }
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    id="specialty"
                    name="specialty"
                    label="Specialty"
                    value={formik.values.specialty}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.specialty && Boolean(formik.errors.specialty)
                    }
                    helperText={
                      formik.touched.specialty && formik.errors.specialty
                    }
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    id="aadhar"
                    name="aadhar"
                    label="Aadhar"
                    value={formik.values.aadhar}
                    onChange={(e) => {
                      // Only allow digits and limit to 12 digits
                      const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                      formik.setFieldValue("aadhar", value);
                    }}
                    onBlur={formik.handleBlur}
                    inputProps={{ maxLength: 12 }}
                    error={formik.touched.aadhar && Boolean(formik.errors.aadhar)}
                    helperText={formik.touched.aadhar && formik.errors.aadhar}
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    id="mobile"
                    name="mobile"
                    label="Contact Number"
                    value={formik.values.mobile}
                    onChange={(e) => {
                      // Only allow digits and limit to 10 digits
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      formik.setFieldValue("mobile", value);
                    }}
                    inputProps={{ maxLength: 10 }}
                    error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                    helperText={formik.touched.mobile && formik.errors.mobile}
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    id="registrationNumber"
                    name="registrationNumber"
                    label="Registration Number"
                    value={formik.values.registrationNumber}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.registrationNumber &&
                      Boolean(formik.errors.registrationNumber)
                    }
                    helperText={
                      formik.touched.registrationNumber &&
                      formik.errors.registrationNumber
                    }
                  />
                </Grid>

                {/* <Grid item xs={6}>
                  <TextField
                    id="departmentId"
                    name="departmentId"
                    fullWidth
                    select 
                    label="Department"
                    value={formik.values.departmentId}
                    onChange={async (e) => {
                      formik.setFieldValue("departmentId", e.target.value);
                    }}
                    error={
                      formik.touched.departmentId &&
                      Boolean(formik.errors.departmentId)
                    }
                    helperText={
                      formik.touched.departmentId && formik.errors.departmentId
                    }
                  >
                    {visibleDepartments.length > 0 && visibleDepartments.map((departmentId) => (
                      <MenuItem
                        key={departmentId["id"]}
                        value={departmentId["id"]}
                      >
                        {departmentId["name"]}
                      </MenuItem>
                    )) }
                  </TextField>
                </Grid> */}

                <Grid item xs={4}>
                  <TextField
                      id="departmentId"
                      name="departmentId"
                      fullWidth
                      size="small"
                      select 
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) => {
                          if (!Array.isArray(selected) || selected.length === 0) {
                            return "";
                          }
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((id) => {
                                const dept = department_list.find(d => Number(d.id) === Number(id));
                                return (
                                  <Chip
                                    key={id}
                                    label={dept?.name || `ID: ${id}`}
                                    onDelete={(e) => {
                                      e.stopPropagation();
                                      const newSelectedIds = selected.filter(selectedId => Number(selectedId) !== Number(id));
                                      formik.setFieldValue("departmentId", newSelectedIds);
                                      
                                      // Update departmentName
                                      const selectedNames = newSelectedIds.map(selectedId => {
                                        const d = department_list.find(dept => Number(dept.id) === Number(selectedId));
                                        return d?.name || "";
                                      }).filter(Boolean);
                                      formik.setFieldValue("departmentName", selectedNames.join(", "));
                                    }}
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                    }}
                                    size="small"
                                    sx={{ 
                                      backgroundColor: '#1976d2',
                                      color: 'white',
                                      '& .MuiChip-deleteIcon': {
                                        color: 'white',
                                        '&:hover': {
                                          color: '#ffebee'
                                        }
                                      }
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          );
                        },
                      }}
                      label="Department"
                      value={formik.values.departmentId}
                      onChange={(e) => {
                        const selectedIds = e.target.value;
                        formik.setFieldValue("departmentId", selectedIds);

                        // Set the departmentName hidden field with comma-separated names
                        const selectedNames = selectedIds.map(id => {
                          const dept = department_list.find(d => Number(d.id) === Number(id));
                          return dept?.name || "";
                        }).filter(Boolean);
                        formik.setFieldValue("departmentName", selectedNames.join(", "));
                      }}
                      error={formik.touched.departmentId && Boolean(formik.errors.departmentId)}
                      helperText={formik.touched.departmentId && formik.errors.departmentId}
                    >
                      {visibleDepartments.length > 0 && visibleDepartments.map((department) => (
                        <MenuItem key={department.id} value={Number(department.id)}>
                          {department.name}
                        </MenuItem>
                      ))}
                    </TextField>

                  {/* <TextField
                    type="hidden"
                    id="departmentName"
                    name="departmentName"
                    value={formik.values.departmentName}
                  /> */}
                </Grid>

                <Grid item xs={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Joining Date"
                      format="YYYY-MM-DD"
                      value={formik.values.joiningDate}
                      onChange={(value) =>
                        formik.setFieldValue("joiningDate", value)
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          error:
                            formik.touched.joiningDate &&
                            Boolean(formik.errors.joiningDate),
                          helperText:
                            formik.touched.joiningDate &&
                            formik.errors.joiningDate,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    id="address"
                    name="address"
                    label="Address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.address && Boolean(formik.errors.address)
                    }
                    helperText={formik.touched.address && formik.errors.address}
                  />
                </Grid>

                {(() => {
                  // Check if updating a doctor
                  if (!doctor_info || !doctor_info.id) {
                    return null; // Don't show password field when creating new doctor
                  }

                  // Check if current user is admin, super admin, or doctor
                  const currentUserRole = (user?.roleName || "").toLowerCase();
                  const normalizedUserRole = currentUserRole.replace(/[\s_]+/g, "_");
                  const canChangePassword = 
                    currentUserRole === "admin" || 
                    normalizedUserRole === "super_admin" ||
                    currentUserRole === "doctor" ||
                    currentUserRole === "surgeon";

                  // Only show password field if user can change passwords
                  if (!canChangePassword) {
                    return null;
                  }

                  // Show password field for admin, super admin, and doctor users
                  return (
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        size="small"
                        id="password"
                        name="password"
                        label="Password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.password && Boolean(formik.errors.password)
                        }
                        helperText={formik.touched.password && formik.errors.password}
                      />
                    </Grid>
                  );
                })()}

                <Grid item xs={4}>
                  <TextField
                    id="userRole"
                    name="userRole"
                    fullWidth
                    size="small"
                    select
                    label="User Role"
                    value={formik.values.userRole}
                    onChange={(e) => {
                      formik.setFieldValue("userRole", e.target.value);
                    }}
                    error={formik.touched.userRole && Boolean(formik.errors.userRole)}
                    helperText={formik.touched.userRole && formik.errors.userRole}
                  >
                    {(() => {
                      // Surgeon role is IPD/surgical (Premium). Keep current role when editing.
                      const availableRoles = filterDoctorRolesForPackage(
                        ["doctor", "surgeon"],
                        {
                          isPremium:
                            isPremiumPackage || subscriptionType === "PREMIUM",
                          keepRole: doctor_info?.userRole || formik.values.userRole,
                        }
                      );

                      return availableRoles.map((role) => (
                        <MenuItem key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </MenuItem>
                      ));
                    })()}
                  </TextField>
                </Grid>

                {/* Subscription Role Limit Warning */}
                {roleLimitCheck && roleLimitCheck.limit !== undefined && roleLimitCheck.limit !== -1 && (
                  <Grid item xs={12}>
                    <Alert 
                      severity={!roleLimitCheck.allowed || roleLimitCheck.limit === 0 ? "error" : "info"}
                      sx={{ mt: 0.5, py: 0, "& .MuiAlert-message": { py: 0.5, fontSize: "0.8rem" } }}
                    >
                      {roleLimitCheck.limit === 0 ? (
                        <>
                          <strong>No Users Allowed:</strong> This subscription does not allow {doctor_info?.id ? "assigning" : "creating"} {formik.values.userRole} users.
                        </>
                      ) : !roleLimitCheck.allowed ? (
                        <>
                          <strong>Limit Reached:</strong> Cannot {doctor_info?.id ? "change role to" : "create more"} {formik.values.userRole} users. 
                          Current: {roleLimitCheck.currentCount}/{roleLimitCheck.limit}
                        </>
                      ) : (
                        <>
                          <strong>Subscription Limit:</strong> {formik.values.userRole} users: {roleLimitCheck.currentCount}/{roleLimitCheck.limit} used
                        </>
                      )}
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 0.25 }}>
                    Select Visiting Hours
                  </Typography>
                </Grid>

                <FormikProvider value={formik}>
                  <FieldArray name="visitingSchedules">
                    {({ push, remove, form }) => (
                      <>
                        {formik.values.visitingSchedules.map(
                          (schedule, index) => (
                            <Grid
                              item
                              xs={12}
                              display={"flex"}
                              flexDirection={"row"}
                              gap={1}
                              alignItems="flex-start"
                            >
                              <Grid item xs={4}>
                                <TextField
                                  select
                                  size="small"
                                  label="Select Day"
                                  fullWidth
                                  name={`visitingSchedules.${index}.day`}
                                  value={schedule.day}
                                  onChange={(e) => {
                                    const selectedDay = e.target.value;
                                    const currentStartTime = schedule.start_time;
                                    const currentEndTime = schedule.end_time;
                                    
                                    // Handle Weekdays selection - keep as "Weekdays" in UI, expand to individual days on submit
                                    if (selectedDay === "Weekdays") {
                                      // Remove any existing individual weekday entries
                                      const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
                                      const updatedSchedules = formik.values.visitingSchedules.filter(
                                        s => !weekdays.includes(s.day)
                                      );
                                      
                                      // Update current entry to "Weekdays" (or add if it was removed)
                                      const currentIndex = updatedSchedules.findIndex((s, idx) => idx === index || s.day === "Weekdays");
                                      if (currentIndex >= 0) {
                                        updatedSchedules[currentIndex] = {
                                          day: "Weekdays",
                                          start_time: currentStartTime,
                                          end_time: currentEndTime,
                                        };
                                      } else {
                                        updatedSchedules.splice(index, 0, {
                                          day: "Weekdays",
                                          start_time: currentStartTime,
                                          end_time: currentEndTime,
                                        });
                                      }
                                      
                                      formik.setFieldValue("visitingSchedules", updatedSchedules);
                                    }
                                    // Handle Weekend selection - keep as "Weekend" in UI, expand to individual days on submit
                                    else if (selectedDay === "Weekend") {
                                      // Remove any existing individual weekend day entries
                                      const weekendDays = ["Saturday", "Sunday"];
                                      const updatedSchedules = formik.values.visitingSchedules.filter(
                                        s => !weekendDays.includes(s.day)
                                      );
                                      
                                      // Update current entry to "Weekend" (or add if it was removed)
                                      const currentIndex = updatedSchedules.findIndex((s, idx) => idx === index || s.day === "Weekend");
                                      if (currentIndex >= 0) {
                                        updatedSchedules[currentIndex] = {
                                          day: "Weekend",
                                          start_time: currentStartTime,
                                          end_time: currentEndTime,
                                        };
                                      } else {
                                        updatedSchedules.splice(index, 0, {
                                          day: "Weekend",
                                          start_time: currentStartTime,
                                          end_time: currentEndTime,
                                        });
                                      }
                                      
                                      formik.setFieldValue("visitingSchedules", updatedSchedules);
                                    }
                                    // Handle individual day selection
                                    else {
                                      // If selecting an individual day, remove it from any group it might be in
                                      const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
                                      const weekendDays = ["Saturday", "Sunday"];
                                      
                                      let updatedSchedules = [...formik.values.visitingSchedules];
                                      
                                      // Remove group entry if selecting a day from that group
                                      if (weekdays.includes(selectedDay)) {
                                        updatedSchedules = updatedSchedules.filter(s => s.day !== "Weekdays");
                                      } else if (weekendDays.includes(selectedDay)) {
                                        updatedSchedules = updatedSchedules.filter(s => s.day !== "Weekend");
                                      }
                                      
                                      // Update current entry
                                      updatedSchedules[index] = {
                                        ...updatedSchedules[index],
                                        day: selectedDay,
                                      };
                                      
                                      formik.setFieldValue("visitingSchedules", updatedSchedules);
                                    }
                                  }}
                                  error={Boolean(
                                    formik.touched.visitingSchedules?.[index]
                                      ?.day &&
                                      formik.errors.visitingSchedules?.[index]
                                        ?.day
                                  )}
                                  helperText={
                                    formik.touched.visitingSchedules?.[index]
                                      ?.day &&
                                    formik.errors.visitingSchedules?.[index]?.day
                                  }
                                >
                                  <MenuItem value="">
                                    <em>Select Day</em>
                                  </MenuItem>
                                  {daysOfWeek.map((day) => (
                                    <MenuItem key={day} value={day}>
                                      {day}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={4}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <MobileTimePicker
                                    label="Start Time"
                                    value={schedule.start_time || null}
                                    onChange={(value) => {
                                      // Update current schedule
                                      formik.setFieldValue(
                                        `visitingSchedules.${index}.start_time`,
                                        value
                                      );
                                      // Mark field as touched to clear validation errors
                                      formik.setFieldTouched(`visitingSchedules.${index}.start_time`, true);
                                      
                                      // No need to sync for "Weekdays" or "Weekend" as they're stored as single entries
                                      // Individual day changes are handled separately
                                    }}
                                    slotProps={{
                                      ...COMPACT_MOBILE_TIME_PICKER_SLOT_PROPS,
                                      textField: {
                                        fullWidth: true,
                                        size: "small",
                                        error: Boolean(
                                          formik.touched.visitingSchedules?.[
                                            index
                                          ]?.start_time &&
                                            formik.errors.visitingSchedules?.[
                                              index
                                            ]?.start_time
                                        ),
                                        helperText:
                                          formik.touched.visitingSchedules?.[
                                            index
                                          ]?.start_time &&
                                          formik.errors.visitingSchedules?.[index]
                                            ?.start_time,
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Grid>
                              <Grid item xs={4}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <MobileTimePicker
                                    label="End Time"
                                    value={schedule.end_time || null}
                                    onChange={(value) => {
                                      // Update current schedule
                                      formik.setFieldValue(
                                        `visitingSchedules.${index}.end_time`,
                                        value
                                      );
                                      // Mark field as touched to clear validation errors
                                      formik.setFieldTouched(`visitingSchedules.${index}.end_time`, true);
                                      
                                      // No need to sync for "Weekdays" or "Weekend" as they're stored as single entries
                                      // Individual day changes are handled separately
                                    }}
                                    slotProps={{
                                      ...COMPACT_MOBILE_TIME_PICKER_SLOT_PROPS,
                                      textField: {
                                        fullWidth: true,
                                        size: "small",
                                        error: Boolean(
                                          formik.touched.visitingSchedules?.[
                                            index
                                          ]?.end_time &&
                                            formik.errors.visitingSchedules?.[
                                              index
                                            ]?.end_time
                                        ),
                                        helperText:
                                          formik.touched.visitingSchedules?.[
                                            index
                                          ]?.end_time &&
                                          formik.errors.visitingSchedules?.[index]
                                            ?.end_time,
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Grid>
                              <Grid
                                item
                                display={"flex"}
                                justifyContent={"center"}
                                alignItems={"center"}
                              >
                                <IconButton onClick={() => remove(index)}>
                                  <DeleteForeverOutlined />
                                </IconButton>
                              </Grid>
                            </Grid>
                          )
                        )}
                        <Grid item xs={12}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              push({ day: "", start_time: null, end_time: null })
                            }
                          >
                            Add More
                          </Button>
                        </Grid>
                      </>
                    )}
                  </FieldArray>
                </FormikProvider>

                <Grid item xs={12}>
                  <FormModalActions
                    onCancel={handelCloseModalState}
                    loading={loading}
                    submitDisabled={
                      loading ||
                      checkingLimit ||
                      (roleLimitCheck &&
                        roleLimitCheck.limit !== -1 &&
                        (roleLimitCheck.limit === 0 || !roleLimitCheck.allowed))
                    }
                    onSubmit={async () => {
                      const errors = await formik.validateForm();
                      console.log('[AddNewDoctor] Form validation errors:', errors);

                      if (Object.keys(errors).length > 0) {
                        touchFormikErrorFields(formik, errors);
                        formik.values.visitingSchedules.forEach((schedule, index) => {
                          if (schedule.day && schedule.day.trim() !== "" && schedule.day !== "Weekdays" && schedule.day !== "Weekend") {
                            formik.setFieldTouched(`visitingSchedules.${index}.start_time`, true);
                            formik.setFieldTouched(`visitingSchedules.${index}.end_time`, true);
                          }
                        });
                        return;
                      }
                      formik.submitForm();
                    }}
                  />
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </BasicModal>

      { /* Credentials Popup */ }
      <Dialog
        open={showCredentialsModal}
        onClose={() => setShowCredentialsModal(false)}
        {...STANDARD_FORM_DIALOG_PROPS}
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Doctor Created</DialogTitle>
        <DialogContent>
          <Typography><strong>Mobile:</strong>{createdDoctor.mobile}</Typography>
          <Typography><strong>Password:</strong>{createdDoctor.password}</Typography>
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button
            onClick={() => {
              setShowCredentialsModal(false);
              handelCloseModalState();
            }}
            variant="outlined"
            color="primary"
            sx={FORM_CANCEL_BUTTON_SX}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddNewDoctor;